import { Call, Device } from '@twilio/voice-sdk';

import { KrispAudioProcessor } from '../krispAudioProcessor';
import { VoiceEventEmitter } from './emitter';
import type {
  VoiceCallParams,
  VoiceError,
  VoiceEventHandler,
  VoiceEventName,
  VoiceProviderService,
} from './types';

// Edge ordering optimized for India-based users.
// Singapore is the closest Twilio edge to India (~5000km); Tokyo and Sydney
// cover APAC fallback; Frankfurt and Ashburn act as global fallbacks.
const EDGE_LOCATIONS = ['singapore', 'tokyo', 'sydney', 'frankfurt', 'ashburn'];

// While a call exists, the SDK keeps trying to re-establish signaling on the
// current edge for this duration before falling back to other edges.
const SIGNALING_RECONNECT_TIMEOUT_MS = 30_000;

// Fire `tokenWillExpire` 30s before TTL so we have time to refresh the JWT
// even during a long reconnect attempt.
const TOKEN_REFRESH_LEAD_MS = 30_000;

/**
 * Twilio implementation of the voice seam.
 *
 * Behaviour is carried over unchanged from the previous twilio.service module;
 * what is new is that the SDK's `Device` and `Call` no longer escape to the
 * caller. Their events are translated onto the normalised names instead, which
 * is what lets the dialer drive either carrier through one surface.
 */
export class TwilioVoiceProvider implements VoiceProviderService {
  private device: Device | null = null;
  private currentCall: Call | null = null;
  private isInitialized = false;
  private noiseCancellationProcessor: KrispAudioProcessor | null = null;
  private readonly emitter = new VoiceEventEmitter();

  isSupported(): boolean {
    return Device.isSupported;
  }

  on<E extends VoiceEventName>(event: E, handler: VoiceEventHandler<E>): () => void {
    return this.emitter.on(event, handler);
  }

  async initializeWithToken(token: string): Promise<void> {
    if (this.isInitialized || this.device || this.currentCall) {
      this.destroy();
    }

    try {
      this.device = new Device(token, {
        edge: EDGE_LOCATIONS,
        codecPreferences: ['opus', 'pcmu'] as any,
        // Enable edge fallback after 30s of failed signaling reconnect attempts.
        maxCallSignalingTimeoutMs: SIGNALING_RECONNECT_TIMEOUT_MS,
        // Surface precise Twilio error codes on `error` events for diagnostics.
        enableImprovedSignalingErrorPrecision: true,
        // Give ourselves a wider window to refresh the access token mid-call.
        tokenRefreshMs: TOKEN_REFRESH_LEAD_MS,
        // Warn the user before they navigate away from an active call.
        closeProtection: 'A call is in progress. Are you sure you want to leave?',
      } as any);
      this.isInitialized = true;

      // Device-level events are the ones that survive a call: transport and
      // signalling failures, and the credential nearing expiry.
      this.device.on('error', (err: VoiceError) =>
        this.emitter.emit('error', { source: 'device', error: err }),
      );
      this.device.on('tokenWillExpire', () => this.emitter.emit('tokenWillExpire', undefined));

      // Noise cancellation is on by default for every session. Users can
      // toggle it off mid-session as an escape hatch, but the preference
      // does not persist — every reload starts with NC on. Failures here
      // are non-fatal: the call still works without NC.
      await this.attachNoiseCancellationProcessor();
    } catch (error) {
      console.error('Failed to initialize Twilio Device with token:', error);
      throw error;
    }
  }

  /**
   * Refresh the Device's access token without tearing down the current call.
   * Used in response to `tokenWillExpire` to keep long-running or reconnecting
   * calls authenticated.
   */
  updateToken(token: string): void {
    if (!this.device) {
      throw new Error('Voice device not initialized');
    }
    this.device.updateToken(token);
  }

  async makeCall(params: VoiceCallParams): Promise<void> {
    if (!this.device || !this.isInitialized) {
      throw new Error('Voice device not initialized. Please initialize first.');
    }

    if (this.currentCall) {
      throw new Error('A call is already in progress');
    }

    try {
      const callParams: any = {
        params: {
          To: params.to,
          From: params.from,
          ...(params.callId && { CallId: params.callId }),
        },
      };

      const call = await this.device.connect(callParams);
      if (!call) {
        throw new Error('Failed to initiate call');
      }

      this.currentCall = call;
      this.attachCallListeners(call);
    } catch (error) {
      console.error('Failed to make call:', error);
      throw error;
    }
  }

  /** Translate the SDK's per-call events onto the normalised names. */
  private attachCallListeners(call: Call): void {
    call.on('accept', () => this.emitter.emit('accept', undefined));
    call.on('disconnect', () => this.emitter.emit('disconnect', undefined));
    call.on('cancel', () => this.emitter.emit('cancel', undefined));
    call.on('reconnecting', (err: VoiceError) => this.emitter.emit('reconnecting', err));
    call.on('reconnected', () => this.emitter.emit('reconnected', undefined));
    call.on('error', (err: VoiceError) =>
      this.emitter.emit('error', { source: 'call', error: err }),
    );
    call.on('warning', (name: string, data?: unknown) =>
      this.emitter.emit('warning', { name, data }),
    );
    call.on('warning-cleared', (name: string) => this.emitter.emit('warningCleared', name));
    call.on('sample', (sample: any) => this.emitter.emit('sample', sample));
    call.on('mute', (isMuted: boolean) => this.emitter.emit('mute', isMuted));
  }

  hangUp(): void {
    if (this.currentCall) {
      this.currentCall.disconnect();
      this.currentCall = null;
    }
  }

  sendDigits(digits: string): void {
    if (this.currentCall && this.currentCall.status() === 'open') {
      this.currentCall.sendDigits(digits);
    }
  }

  toggleMute(): boolean {
    if (this.currentCall) {
      const isMuted = this.currentCall.isMuted();
      this.currentCall.mute(!isMuted);
      return !isMuted;
    }
    return false;
  }

  isCallInProgress(): boolean {
    return this.currentCall !== null && this.currentCall.status() !== 'closed';
  }

  networkRegion(): string | null {
    return this.device?.edge ?? null;
  }

  supportsNoiseCancellation(): boolean {
    return true;
  }

  isNoiseCancellationEnabled(): boolean {
    return this.noiseCancellationProcessor !== null;
  }

  async setNoiseCancellationEnabled(enabled: boolean): Promise<void> {
    if (!this.device) {
      throw new Error('Voice device not initialized');
    }

    if (enabled && !this.noiseCancellationProcessor) {
      await this.attachNoiseCancellationProcessor();
    } else if (!enabled && this.noiseCancellationProcessor) {
      const processor = this.noiseCancellationProcessor;
      this.noiseCancellationProcessor = null;
      await this.device.audio?.removeProcessor(processor);
    }
  }

  private async attachNoiseCancellationProcessor(): Promise<void> {
    if (!this.device || this.noiseCancellationProcessor) return;
    const processor = new KrispAudioProcessor();
    try {
      await this.device.audio?.addProcessor(processor);
      this.noiseCancellationProcessor = processor;
    } catch (err) {
      console.error('Failed to attach noise cancellation processor:', err);
    }
  }

  destroy(): void {
    if (this.currentCall) {
      this.currentCall.disconnect();
      this.currentCall = null;
    }

    if (this.device) {
      if (this.noiseCancellationProcessor) {
        // Best-effort removal; Device.destroy() will tear down the audio
        // graph anyway, but explicit removal lets the processor close its
        // AudioContext cleanly.
        this.device.audio?.removeProcessor(this.noiseCancellationProcessor).catch(() => {});
        this.noiseCancellationProcessor = null;
      }
      this.device.destroy();
      this.device = null;
    }

    this.isInitialized = false;
    // Subscribers are per-session: a destroyed service must not keep calling
    // handlers that belong to a call the dialer has already finished with.
    this.emitter.clear();
  }
}
